import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useReviewDetail } from '../../hooks/useReviewDetail';
import type { ReviewDetail } from '../../hooks/useReviewDetail';
import ReviewDialog from '../../components/ReviewDialog/ReviewDialog';
import EditableTable from '../../components/EditableTable/EditableTable';
import LanguageTabs from '../../components/LanguageTabs/LanguageTabs';
import EditKeyDialog from '../../components/EditKeyDialog/EditKeyDialog';
import TranslationKeyDialog from '../../components/TranslationKeyDialog/TranslationKeyDialog';
import LanguageCompletion from '../../components/LanguageCompletion/LanguageCompletion';
import { supabase } from '../../lib/supabase';
import {
  fetchTranslationKeyById,
  fetchTranslationStrings,
  updateTranslationKey,
  upsertTranslationString,
  deleteTranslationKey,
  createTranslationKey,
  checkKeyExists,
  getProjectKeyCount,
  TranslationKey,
  TranslationString
} from '../../services/translationService';
import './ReviewDetail.css';
import { useLanguages } from '../../context/LanguageContext';
import { useProjectPermissions } from '../../hooks/useProjectPermissions';
import { useNotification } from '../../context/NotificationContext';
import EmptyStateEnhanced from '../../components/EmptyStateEnhanced/EmptyStateEnhanced';
import { useWorkspace } from '../../context/WorkspaceContext';
import { aiTranslationService } from '../../services/translationAI';
import { setContentWithHighlightedPlaceholders } from '../../utils/placeholderUtils';
import { batchTranslationService, type BatchTranslationRequest } from '../../services/batchTranslationService';
import { useSubscription, FREE_TIER_KEY_LIMIT } from '../../hooks/useSubscription';
import SubscriptionDialog from '../../components/SubscriptionDialog/SubscriptionDialog';

export interface ReviewDetailProps {
  id?: string;
  title?: string;
  owner?: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface TranslationData {
  key: TranslationKey;
  strings: TranslationString[];
}

const ReviewDetailComponent: React.FC<ReviewDetailProps> = (props) => {
  const { reviewId } = useParams<{ reviewId: string }>();
  const navigate = useNavigate();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const { review, isLoading, toggleReviewCompletion, updateReview, setReview } = useReviewDetail(reviewId);
  const [translationData, setTranslationData] = useState<TranslationData[]>([]);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [keyToEdit, setKeyToEdit] = useState<TranslationKey | null>(null);
  const [isEditKeyDialogOpen, setIsEditKeyDialogOpen] = useState(false);
  const [isEditingKey, setIsEditingKey] = useState(false);
  const { 
    selectedLanguage,
    firstLanguage,
    getLanguageName,
    setSelectedLanguage: handleLanguageChange,
    availableLanguages,
    addLanguage
  } = useLanguages();

  const { isAdmin, canEditLanguages } = useProjectPermissions();
  const [isKeyDialogOpen, setIsKeyDialogOpen] = useState(false);
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const { showNotification } = useNotification();
  const { projects } = useWorkspace();
  const [translatingCells] = useState<Set<string>>(new Set());
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- setter used for batch translate
  const [isTranslating, setIsTranslating] = useState(false);
  const [activeJobs, setActiveJobs] = useState<Map<string, { jobId: string; progress: any }>>(new Map());
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);
  const { hasActiveSubscription, isLoading: isSubscriptionLoading } = useSubscription();
  const [projectKeyCount, setProjectKeyCount] = useState<number | null>(null);

  useEffect(() => {
    if (!review?.project_id) {
      setProjectKeyCount(null);
      return;
    }
    let cancelled = false;
    getProjectKeyCount(review.project_id).then((count) => {
      if (!cancelled) setProjectKeyCount(count);
    }).catch(() => {
      if (!cancelled) setProjectKeyCount(null);
    });
    return () => { cancelled = true; };
  }, [review?.project_id]);

  const showSubscriptionBlocker = !isSubscriptionLoading && !hasActiveSubscription && projectKeyCount !== null && projectKeyCount >= FREE_TIER_KEY_LIMIT;

  // Use props if provided, otherwise use review data
  const id = props.id || reviewId;
  const title = props.title || (review?.title);
  const owner = props.owner;
  const description = props.description || review?.description;
  const createdAt = props.createdAt || review?.created_at;
  const updatedAt = props.updatedAt || review?.last_updated_at;
  const isCompleted = review ? review.completed : false;
  
  // Set up the TopNavBar props when the component mounts or updates
  useEffect(() => {
    // Find the global event dispatcher to communicate with the main TopNavBar
    const setNavBarState = (state: any) => {
      const event = new CustomEvent('update-top-nav', { 
        detail: state
      });
      window.dispatchEvent(event);
    };
    
    // Set the review detail view state immediately for proper layout
    setNavBarState({
      onBackClickProp: handleClose,
      isReviewDetailViewProp: true,
      reviewId: id
    });
    
    // Only set the title when it's available
    if (title) {
      setNavBarState({
        reviewTitle: title,
        onBackClickProp: handleClose,
        isReviewCompleted: isCompleted,
        onEditClick: handleOpenEditDialog,
        onCompleteClick: handleToggleCompletion,
        isReviewDetailViewProp: true,
        reviewId: id
      });
    } else if (!review && !props.title && !isLoading) {
      // Only show "Review not found" when we're sure it's not found
      setNavBarState({
        reviewTitle: "Review not found",
        onBackClickProp: handleClose,
        isReviewDetailViewProp: true,
        reviewId: id
      });
    }
    
    // Clean up when component unmounts
    return () => {
      setNavBarState({
        isReviewDetailViewProp: false
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: sync nav bar with title/completed state
  }, [title, isCompleted, isLoading, review, props.title]);

  // Subscribe to real-time translation updates for immediate UI feedback
  useEffect(() => {
    if (!review?.project_id) return;

    // Throttle rapid updates to prevent browser resource exhaustion
    let updateQueue: any[] = [];
    let updateTimeout: NodeJS.Timeout | null = null;

    const processUpdates = () => {
      if (updateQueue.length === 0) return;

      console.log(`🔄 Processing ${updateQueue.length} real-time translation updates in review`);
      
      // Update all queued translations in one state update
      setTranslationData(prevData => {
        let newData = [...prevData];
        
        updateQueue.forEach(newTranslation => {
          newData = newData.map(data => {
            if (data.key.id === newTranslation.key_id) {
              const updatedStrings = data.strings.filter(s => 
                !(s.language_code === newTranslation.language_code)
              );
              updatedStrings.push(newTranslation);
              
              return {
                ...data,
                strings: updatedStrings
              };
            }
            return data;
          });
        });
        
        return newData;
      });

      // Show success indicators for all updated translations
      updateQueue.forEach(newTranslation => {
        const successClass = 'translation-success';
        const cellElement = document.querySelector(
          `.editable-table-cell[data-row="${newTranslation.key_id}"][data-col="${newTranslation.language_code}"]`
        );
        if (cellElement) {
          cellElement.classList.add(successClass);
          setTimeout(() => {
            cellElement.classList.remove(successClass);
          }, 1000);
        }
      });

      // Clear the queue
      updateQueue = [];
      updateTimeout = null;
    };

    const unsubscribe = batchTranslationService.subscribeToTranslationUpdates(
      review.project_id,
      (newTranslation) => {
        // Add to queue and throttle updates
        updateQueue.push(newTranslation);
        
        if (updateTimeout) {
          clearTimeout(updateTimeout);
        }
        
        // Process updates after a short delay to batch them (increased delay for better performance)  
        updateTimeout = setTimeout(processUpdates, 300);
      }
    );

    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      unsubscribe();
    };
  }, [review?.project_id]);
  
  // Load translation keys and their strings when review is loaded
  useEffect(() => {
    const loadKeys = async () => {
      if (!review?.keys) return;
      
      setIsLoadingKeys(true);
      try {
        // Fetch translation keys - handle the case where keys is a number or array
        const keyIds = Array.isArray(review.keys) ? review.keys : [review.keys];
        
        // Fetch each key and its strings
        const keysWithStrings = await Promise.all(
          keyIds.map(async (keyId) => {
            const key = await fetchTranslationKeyById(keyId);
            if (!key) return null;
            
            const strings = await fetchTranslationStrings(keyId);
            return { key, strings };
          })
        );
        
        // Filter out any null values and set the translation data
        const validData = keysWithStrings.filter((data): data is TranslationData => data !== null);
        setTranslationData(validData);
      } catch (err) {
        console.error('Error loading translation keys:', err);
      } finally {
        setIsLoadingKeys(false);
      }
    };
    
    loadKeys();
  }, [review]);
  
  // Handle opening the key dialog
  useEffect(() => {
    const handleOpenKeyDialog = (event: CustomEvent<{ reviewId: string }>) => {
      if (event.detail.reviewId === id) {
        setIsKeyDialogOpen(true);
      }
    };

    window.addEventListener('open-key-dialog', handleOpenKeyDialog as EventListener);
    return () => {
      window.removeEventListener('open-key-dialog', handleOpenKeyDialog as EventListener);
    };
  }, [id]);
  
  const handleClose = () => {
    // Navigate immediately without delay
    navigate('/app/reviews');
  };
  
  const handleToggleCompletion = async () => {
    if (reviewId && review) {
      await toggleReviewCompletion();
    }
  };
  
  const handleOpenEditDialog = () => {
    setIsEditDialogOpen(true);
  };
  
  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
  };
  
  const handleSaveEdit = async (title: string, description: string) => {
    if (review) {
      await updateReview({ title, description });
      setIsEditDialogOpen(false);
    }
  };
  
  // Handle cell changes in the table
  const handleCellChange = async (rowKey: string, columnKey: string, value: string) => {
    if (showSubscriptionBlocker) {
      setIsSubscriptionDialogOpen(true);
      return;
    }

    try {
      if (columnKey === 'key') {
        // Update key name
        const updatedKey = await updateTranslationKey(rowKey, value);
        if (updatedKey) {
          setTranslationData(prev => prev.map(data => 
            data.key.id === rowKey ? { ...data, key: updatedKey } : data
          ));
          showNotification('Key updated');
        }
      } else {
        // Update translation string using the actual language code
        if (!review?.project_id) {
          throw new Error('No project ID available');
        }
        const updatedString = await upsertTranslationString(rowKey, columnKey, value, review.project_id);
        if (updatedString) {
          setTranslationData(prev => prev.map(data => {
            if (data.key.id === rowKey) {
              const existingStringIndex = data.strings.findIndex(s => s.language_code === columnKey);
              const newStrings = [...data.strings];
              
              if (existingStringIndex >= 0) {
                newStrings[existingStringIndex] = updatedString;
              } else {
                newStrings.push(updatedString);
              }
              
              return { ...data, strings: newStrings };
            }
            return data;
          }));
          showNotification('Translation updated');
        }
      }
    } catch (err) {
      showNotification('Failed to update');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- batch translate handler for UI
  const handleAITranslate = async () => {
    if (!selectedLanguage || !review) return;

    if (showSubscriptionBlocker) {
      setIsSubscriptionDialogOpen(true);
      return;
    }

    // Find all empty cells that need translation
    const cellsToTranslate = translationData.filter(data => {
      const translation = data.strings.find(s => s.language_code === selectedLanguage);
      return !translation || !translation.value.trim();
    });

    if (cellsToTranslate.length === 0) {
      showNotification("No empty translations found");
      return;
    }

    // Show notification about starting the batch translation
    showNotification(`Starting batch translation of ${cellsToTranslate.length} keys...`);
    
    // Set flag to prevent navigation away
    setIsTranslating(true);
    
    try {
      // Prepare batch translation request
      const requestItems = cellsToTranslate
        .map(item => {
          // Get source text from first language - do NOT use key as fallback
          const sourceText = item.strings.find(s => s.language_code === firstLanguage)?.value;
          
          // Only include items that have actual source text and valid source language
          if (!sourceText || !sourceText.trim() || !firstLanguage) {
            return null;
          }
          
          return {
            key_id: item.key.id,
            source_text: sourceText,
            source_language: firstLanguage,
            target_language: selectedLanguage
          };
        })
        .filter((item): item is { key_id: string; source_text: string; source_language: string; target_language: string; } => item !== null); // Remove null items with type guard

      // Find the project to get team_id
      const project = projects.find(p => p.id === review.project_id);
      
      const batchRequest: BatchTranslationRequest = {
        items: requestItems,
        project_id: review.project_id,
        team_id: project?.team_id
      };

      // Add translating class to all cells
      cellsToTranslate.forEach(item => {
        const cellElement = document.querySelector(`.editable-table-cell[data-row="${item.key.id}"][data-col="${selectedLanguage}"]`);
        if (cellElement) {
          cellElement.classList.add('translating');
        }
      });

      // Start batch translation on backend
      const batchId = await batchTranslationService.startBatchTranslation(batchRequest);
      
      showNotification(`Batch translation started. Job ID: ${batchId.slice(0, 8)}...`);

      // Subscribe to real-time progress updates
      const _unsubscribe = batchTranslationService.subscribeToProgress(batchId, {
        onProgress: (progress) => {
          const progressText = `Translating... ${progress.progress_percentage}% (${progress.completed_items}/${progress.total_items})`;
          showNotification(progressText);
        },
        
        onComplete: (progress) => {
          // Refresh the review data to show new translations
          if (review) {
            // You might want to call a refresh function here
            // fetchReviewData(review.id) or similar
          }
          
          // Remove translating class from all cells
          cellsToTranslate.forEach(item => {
            const cellElement = document.querySelector(`.editable-table-cell[data-row="${item.key.id}"][data-col="${selectedLanguage}"]`);
            if (cellElement) {
              cellElement.classList.remove('translating');
            }
          });
          
          setIsTranslating(false);
          
          if (progress.failed_items > 0) {
            showNotification(`✅ Translation complete! ${progress.completed_items} successful, ${progress.failed_items} failed`);
          } else {
            showNotification(`✅ Translation complete! ${progress.completed_items} keys successfully translated`);
          }
        },
        
        onError: (error) => {
          console.error('Batch translation error:', error);
          
          // Remove translating class from all cells
          cellsToTranslate.forEach(item => {
            const cellElement = document.querySelector(`.editable-table-cell[data-row="${item.key.id}"][data-col="${selectedLanguage}"]`);
            if (cellElement) {
              cellElement.classList.remove('translating');
            }
          });
          
          setIsTranslating(false);
          showNotification(`❌ Translation failed: ${error}`);
        }
      });
      void _unsubscribe;

    } catch (error) {
      console.error('Failed to start batch translation:', error);
      setIsTranslating(false);
      showNotification(`❌ Failed to start translation: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      // Remove translating class from all cells
      cellsToTranslate.forEach(item => {
        const cellElement = document.querySelector(`.editable-table-cell[data-row="${item.key.id}"][data-col="${selectedLanguage}"]`);
        if (cellElement) {
          cellElement.classList.remove('translating');
        }
      });
    }
  };

  // Handle editing a key
  const handleEditClick = (keyId: string) => {
    const keyData = translationData.find(data => data.key.id === keyId);
    if (keyData) {
      setKeyToEdit(keyData.key);
      setIsEditKeyDialogOpen(true);
    }
  };

  // Handle saving edited key name
  const handleSaveKeyEdit = async (newKeyName: string) => {
    if (!keyToEdit) return;

    if (showSubscriptionBlocker) {
      setIsSubscriptionDialogOpen(true);
      return;
    }
    
    try {
      setIsEditingKey(true);
      
      // Update the key name
      const updatedKey = await updateTranslationKey(keyToEdit.id, newKeyName);
      
      // Update the state with the new key name
      setTranslationData(prev => prev.map(data => 
        data.key.id === keyToEdit.id ? { ...data, key: updatedKey } : data
      ));

      showNotification('Key updated');
      setIsEditKeyDialogOpen(false);
      setKeyToEdit(null);
    } catch (err) {
      showNotification('Failed to update key');
      throw err;
    } finally {
      setIsEditingKey(false);
    }
  };

  // Handle deleting a key
  const handleDeleteKey = async () => {
    if (!keyToEdit) return;
    
    try {
      await deleteTranslationKey(keyToEdit.id);
      setTranslationData(prev => prev.filter(data => data.key.id !== keyToEdit.id));
      showNotification('Key deleted');
      setIsEditKeyDialogOpen(false);
      setKeyToEdit(null);
    } catch (err) {
      showNotification('Failed to delete key');
      throw err;
    }
  };

  // Handle row selection
  const handleRowSelect = (keyId: string, selected: boolean) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(keyId);
      } else {
        newSet.delete(keyId);
      }
      return newSet;
    });
  };

  // Get cell content based on column
  const getKeyCellContent = (row: TranslationData, columnKey: string) => {
    if (columnKey === 'key') {
      return row.key.key;
    }
    // Use the actual language code from the column key
    return row.strings.find(s => s.language_code === columnKey)?.value || '';
  };

  // Handle re-translate for a single cell
  const handleReTranslate = async (keyId: string, targetLanguage: string) => {
    try {
      // Find the source text
      const data = translationData.find(d => d.key.id === keyId);
      if (!data) return;

      const sourceString = data.strings.find(s => s.language_code === firstLanguage);
      if (!sourceString?.value) {
        showNotification('No source text found');
        return;
      }

      // Translate the text
      const translatedItem = await aiTranslationService.translateText(sourceString.value, targetLanguage);

      // Update the translation
      if (!review?.project_id) {
        throw new Error('No project ID available');
      }
      await handleCellChange(keyId, targetLanguage, translatedItem);

    } catch (error) {
      showNotification('Failed to re-translate');
    }
  };

  // Get cell actions based on column
  const getCellActions = (row: TranslationData, columnKey: string) => {
    if (columnKey === 'key' || columnKey === firstLanguage) {
      return undefined;
    }

    return [
      {
        label: 'Re-translate',
        onClick: () => handleReTranslate(row.key.id, columnKey)
      }
    ];
  };

  // Handle cell translation
  const handleTranslateCell = async (rowKey: string, columnKey: string, sourceText: string) => {
    if (showSubscriptionBlocker) {
      setIsSubscriptionDialogOpen(true);
      return null;
    }

    try {
      const translatedText = await aiTranslationService.translateText(sourceText, columnKey);
      return { result: translatedText };
    } catch (error) {
      showNotification('Failed to translate');
      return null;
    }
  };

  // Define table columns
  const keyColumns = [
    { header: 'Key', key: 'key', editable: true },
    { header: getLanguageName(firstLanguage), key: firstLanguage || 'first', editable: true },
    { header: getLanguageName(selectedLanguage), key: selectedLanguage || 'second', editable: true }
  ];

  // Get container class based on navigation state
  const getContainerClass = () => {
    return 'review-detail-wrapper';
  };

  // Handle saving a new key
  const handleSaveKey = async (keyName: string, defaultValue: string) => {
    if (!review?.project_id) return;

    if (showSubscriptionBlocker) {
      setIsSubscriptionDialogOpen(true);
      return;
    }

    try {
      setIsCreatingKey(true);
      
      // Check if key already exists
      const keyExists = await checkKeyExists(review.project_id, keyName);
      if (keyExists) {
        throw new Error('A key with this name already exists in this project');
      }
      
      // Create the new key
      const newKey = await createTranslationKey(review.project_id, keyName);
      if (!newKey) throw new Error('Failed to create key');

      // Update the review's keys array
      const currentKeys = Array.isArray(review.keys) ? review.keys : [];
      const updatedKeys = [...currentKeys, newKey.id];

      const { error: updateError } = await supabase
        .from('Reviews')
        .update({ keys: updatedKeys })
        .eq('id', review.id);

      if (updateError) throw updateError;

      // If there's a default value, create the translation string
      if (defaultValue && firstLanguage) {
        if (!review?.project_id) {
          throw new Error('No project ID available');
        }
        await upsertTranslationString(newKey.id, firstLanguage, defaultValue, review.project_id);
      }

      // Fetch the new key's strings
      const strings = await fetchTranslationStrings(newKey.id);
      
      // Add the new key and its strings to the translation data
      setTranslationData(prev => [...prev, { key: newKey, strings }]);
      
      showNotification('Key added');
      setIsKeyDialogOpen(false);
    } catch (err) {
      showNotification('Failed to add key');
      throw err;
    } finally {
      setIsCreatingKey(false);
    }
  };

  const handleToggleLanguageComplete = async (languageCode: string) => {
    if (!reviewId || !review) return;

    try {
      const currentLanguages = review.languages_done || [];
      const isCompleted = currentLanguages.includes(languageCode);
      
      let newLanguages: string[];
      if (isCompleted) {
        // Remove the language
        newLanguages = currentLanguages.filter(lang => lang !== languageCode);
      } else {
        // Add the language
        newLanguages = [...currentLanguages, languageCode];
      }

      // Check if all languages are done
      const allLanguagesDone = availableLanguages.every(lang => 
        newLanguages.includes(lang)
      );

      // Update the review with the new languages_done array
      const { error } = await supabase
        .from('Reviews')
        .update({ languages_done: newLanguages })
        .eq('id', reviewId);

      if (error) throw error;

      // Update the review in local state
      setReview((prev: ReviewDetail | null) => {
        if (!prev) return null;
        return {
          ...prev,
          languages_done: newLanguages
        };
      });

      // If all languages are done, mark the task as complete
      if (allLanguagesDone) {
        await toggleReviewCompletion();
      }

      showNotification(isCompleted ? 'Language marked as incomplete' : 'Language marked as complete');
      if (allLanguagesDone) {
        showNotification('All languages completed! Task marked as done.');
      }
    } catch (error) {
      showNotification('Failed to update language completion status');
    }
  };

  // Watch for changes in review.completed and unmark all languages when task is reopened
  useEffect(() => {
    if (review && !review.completed) {
      // If task is reopened (not completed), clear all language completions
      const updateLanguages = async () => {
        try {
          const { error } = await supabase
            .from('Reviews')
            .update({ languages_done: [] })
            .eq('id', reviewId);

          if (error) throw error;

          // Update local state
          setReview((prev: ReviewDetail | null) => {
            if (!prev) return null;
            return {
              ...prev,
              languages_done: []
            };
          });

          showNotification('All languages unmarked');
        } catch (error) {
          showNotification('Failed to unmark languages');
        }
      };

      updateLanguages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when review.completed toggles
  }, [review?.completed]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- cancel handler for UI
  const handleCancelTranslation = () => {
    // Set a flag to stop translation
    window.sessionStorage.setItem('texta_translation_cancelled', 'true');
    
    // Reset the translating state
    setIsTranslating(false);
    
    // Remove translating class from all cells
    const cells = document.querySelectorAll('.editable-table-cell');
    cells.forEach(cell => {
      cell.classList.remove('translating');
    });
    
    // Clear the cancelled flag after a short delay
    setTimeout(() => {
      window.sessionStorage.removeItem('texta_translation_cancelled');
    }, 1000);
    
    showNotification('Translation cancelled');
  };

  // Calculate missing translations for the selected language
  const getMissingTranslationsCount = () => {
    if (!selectedLanguage || !translationData) return 0;
    return translationData.filter(row => {
      const translation = row.strings.find(s => s.language_code === selectedLanguage);
      return !translation || !translation.value.trim();
    }).length;
  };

  // Calculate missing translations for all languages
  const getMissingTranslationsForAllLanguages = () => {
    if (!translationData || !availableLanguages) return [];
    
    return availableLanguages.map(languageCode => {
      const missingCount = translationData.filter(row => {
        const translation = row.strings.find(s => s.language_code === languageCode);
        return !translation || !translation.value.trim();
      }).length;
      
      return {
        language: languageCode,
        languageName: getLanguageName(languageCode),
        missingCount,
        totalKeys: translationData.length,
        canTranslate: languageCode !== firstLanguage && missingCount > 0,
        isLoading: activeJobs.has(languageCode)
      };
    });
  };



  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- cancel per language for UI
  const handleCancelTranslationForLanguage = (language: string) => {
    const job = activeJobs.get(language);
    if (job) {
      // Cancel the specific job
      batchTranslationService.cancelBatchTranslation(job.jobId);
      
      // Remove from active jobs
      setActiveJobs(prev => {
        const newMap = new Map(prev);
        newMap.delete(language);
        return newMap;
      });
      
      showNotification('Translation cancelled');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- table translate handler
  const handleTableTranslate = async (rowKey: string, columnKey: string, translatedText: string) => {
    
    if (!translatedText || !translatedText.trim()) {
      return;
    }
    
    // Find the row data
    const rowData = translationData.find(item => item.key.id === rowKey);
    
    if (rowData) {
      
      // Find and update the cell in the DOM
      const cellElement = document.querySelector(`.editable-table-cell[data-row="${rowKey}"][data-col="${columnKey}"]`);
      
      if (cellElement) {
        // Add translating class to show the magic border
        cellElement.classList.add('translating');
        
        // Set initial empty content before animation
        if (cellElement.getAttribute('contenteditable') === 'true') {
          cellElement.textContent = '';
        }
        
        // Create a unique animation effect using inline style with more pronounced animation
        const animateWords = (text: string) => {
          // Split by spaces but preserve spaces in the output
          const words = text.split(/\s+/);
          return words.map((word: string, index: number) => {
            // Calculate staggered delay based on word position
            const delay = 0.08 * index;
            // Make animation more dramatic for longer words
            const distance = Math.min(10 + word.length, 20);
            
            return `<span 
              class="word-animate" 
              style="
                animation-delay: ${delay}s; 
                animation-duration: 0.6s;
                display: inline-block; 
                opacity: 0; 
                transform: translateY(${distance}px);
                animation: fadeInUp 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) ${delay}s forwards;
                position: relative;
              "
            >${word}</span>`;
          }).join(' ');
        };
        
        const animatedHTML = animateWords(translatedText);
        
        // Apply animated HTML immediately
        if (cellElement.getAttribute('contenteditable') === 'true') {
          cellElement.innerHTML = animatedHTML;
        }
        
        // For non-contenteditable elements, find and update child elements
        const cellContent = cellElement.querySelector('.cell-content');
        if (cellContent) {
          cellContent.innerHTML = animatedHTML;
        }
        
        // After the animation completes, set the actual text content for editing
        const animationDuration = Math.min(translatedText.split(/\s+/).length * 50 + 1500, 3500); // Cap at 3.5 seconds for long text
        
        // Create a reference to the element to use in the timeout
        const elementForTimeout = cellElement;
        
        setTimeout(() => {
          // Update with plain text after animation finishes
          if (elementForTimeout && elementForTimeout.getAttribute('contenteditable') === 'true') {
            elementForTimeout.textContent = translatedText;
            
            // Trigger data model update
            
            // Save changes to the data model
            handleCellChange(rowKey, columnKey, translatedText);
          }
          
          // Update spans as well
          if (elementForTimeout) {
            const spans = elementForTimeout.querySelectorAll('.cell-content > span:not(.word-animate)');
            spans.forEach(span => {
              span.textContent = translatedText;
            });
            
            // Also update the cell-content's text if there are no spans
            if (spans.length === 0) {
              const cellContent = elementForTimeout.querySelector('.cell-content');
              if (cellContent) {
                cellContent.textContent = translatedText;
                // Add placeholder highlighting after setting the text
                setContentWithHighlightedPlaceholders(cellContent as HTMLElement, translatedText);
              }
            }
          }
        }, animationDuration);
        
        // Store a reference to the element for the timeout
        const elementRef = cellElement;
        
        // Keep the animation visible for a bit, but not too long
        setTimeout(() => {
          if (elementRef) {
            elementRef.classList.remove('translating');
            
            // Ensure the text is still there after animation
            const contentEditableElement = elementRef.getAttribute('contenteditable') === 'true' ? 
              elementRef : 
              elementRef.querySelector('[contenteditable="true"]');
              
            if (contentEditableElement && !contentEditableElement.textContent) {
              contentEditableElement.textContent = translatedText;
            }
            
            // Check cell-content as well and add placeholder highlighting
            const cellContent = elementRef.querySelector('.cell-content');
            if (cellContent) {
              if (!cellContent.textContent) {
                cellContent.textContent = translatedText;
              }
              // Add placeholder highlighting after animation
              setContentWithHighlightedPlaceholders(cellContent as HTMLElement, translatedText);
            }
          }
        }, animationDuration + 1000);
        
        // Update the data model and database immediately
        
        await handleCellChange(rowKey, columnKey, translatedText);
        
        // Force a refresh of the UI to ensure consistency
        setTimeout(() => {
          // Update the translation data to trigger a re-render
          const updatedData = [...translationData];
          
          // Find and update the specific row/column
          const rowIndex = updatedData.findIndex(item => item.key.id === rowKey);
          if (rowIndex !== -1) {
            const stringIndex = updatedData[rowIndex].strings.findIndex(s => s.language_code === columnKey);
            
            // If string exists, update it; otherwise create a new one
            if (stringIndex !== -1) {
              updatedData[rowIndex].strings[stringIndex].value = translatedText;
            } else if (columnKey !== 'key') {
              // Add a new string if it doesn't exist
              updatedData[rowIndex].strings.push({
                id: `temp-${Date.now()}`, // Temporary ID
                key_id: rowKey,
                language_code: columnKey,
                value: translatedText,
                created_at: new Date().toISOString()
              });
            }
          }
          
          setTranslationData(updatedData);
          
          // Add placeholder highlighting after state update
          const cellContent = elementRef?.querySelector('.cell-content');
          if (cellContent) {
            setContentWithHighlightedPlaceholders(cellContent as HTMLElement, translatedText);
          }
        }, animationDuration + 200);
      } else {
        
        // Even if we can't find the cell, still update the data
        await handleCellChange(rowKey, columnKey, translatedText);
      }
    } else {
      
    }
  };

  // Don't render anything if data is still loading
  if (isLoading) {
    return <div className={getContainerClass()}></div>;
  }

  // Don't render content if review is not found
  if (!review && !props.title) {
    return (
      <div className={getContainerClass()}>
        <div className="review-detail-container">
          <div className="review-detail-content">
            <div className="review-detail-not-found">Review not found</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={getContainerClass()}>
      {/* Add the LanguageTabs at the top */}
      {review && firstLanguage && (
        <LanguageTabs 
          languageStats={getMissingTranslationsForAllLanguages()}
          sourceLanguage={firstLanguage || undefined}
          sourceLanguageName={firstLanguage ? getLanguageName(firstLanguage) : undefined}
          sourceMissingCount={getMissingTranslationsCount()}
          selectedLanguage={selectedLanguage || undefined}
          onLanguageSelect={handleLanguageChange}
          
          activeJobs={activeJobs}
          isAdmin={isAdmin}
          canEditLanguages={canEditLanguages}
          onAddLanguage={addLanguage}
          existingLanguages={availableLanguages}
        />
      )}
      
      <div className="review-detail-container">
        <div className="review-detail-content">
          <div className="review-detail-main">
            {title && <h2 className="review-detail-name">{title}</h2>}
            
            {owner && <p className="review-detail-owner">Owned by {owner}</p>}
            
            {description && (
              <div className="review-detail-description">
                <p>{description}</p>
              </div>
            )}

            {/* Language Completion Section - Conditionally render based on keys */}
            {translationData.length > 0 && (
              <div className="review-detail-languages">
                <p className="review-detail-languages-title">Mark as done</p>
                <div className="review-detail-languages-pills">
                  {availableLanguages.map((languageCode: string) => (
                    <LanguageCompletion
                      key={languageCode}
                      languageCode={languageCode}
                      languageName={getLanguageName(languageCode)}
                      isCompleted={review?.languages_done?.includes(languageCode) || false}
                      onToggleComplete={handleToggleLanguageComplete}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Translation Keys Section */}
            <div className="review-detail-keys">
              {isLoadingKeys ? (
                <div className="loading-state">
                  <p>Loading translation keys...</p>
                </div>
              ) : translationData.length > 0 ? (
                <div className="translations-section">
                 
                  <EditableTable
                    columns={keyColumns}
                    data={translationData}
                    getRowKey={(row) => row.key.id}
                    getCellContent={getKeyCellContent}
                    getCellActions={getCellActions}
                    onCellChange={handleCellChange}
                    onDelete={handleEditClick}
                    selectedRows={selectedRows}
                    onRowSelect={handleRowSelect}
                    onTranslateCell={handleTranslateCell}
                    sourceLanguage={firstLanguage}
                    targetLanguage={selectedLanguage}
                    translatingCells={translatingCells}
                  />
                </div>
              ) : (
                <EmptyStateEnhanced
                  title="Add keys to review"
                  subtitle="Select existing keys or create new ones to start localizing content."
                  buttonText="Add key"
                  onButtonClick={() => setIsKeyDialogOpen(true)}
                  fullView={true}
                />
              )}
            </div>
            
            <div className="review-detail-metadata">
              {updatedAt && <p className="review-detail-timestamp">Last updated {updatedAt}</p>}
              {createdAt && <p className="review-detail-timestamp">Created {createdAt}</p>}
            </div>
          </div>
        </div>
      </div>
      
      {/* Add the ReviewDialog for editing */}
      {review && (
        <ReviewDialog 
          isOpen={isEditDialogOpen}
          onClose={handleCloseEditDialog}
          onSave={handleSaveEdit}
          isLoading={isLoading}
          initialTitle={review.title}
          initialDescription={review.description}
        />
      )}

      {/* Add the EditKeyDialog for editing keys */}
      {keyToEdit && (
        <EditKeyDialog
          isOpen={isEditKeyDialogOpen}
          onClose={() => {
            setIsEditKeyDialogOpen(false);
            setKeyToEdit(null);
          }}
          onSave={handleSaveKeyEdit}
          onDelete={handleDeleteKey}
          keyName={keyToEdit.key}
          isLoading={isEditingKey}
        />
      )}

      {/* Add the TranslationKeyDialog */}
      <TranslationKeyDialog
        isOpen={isKeyDialogOpen}
        onClose={() => setIsKeyDialogOpen(false)}
        onSave={handleSaveKey}
        isLoading={isCreatingKey}
        firstLanguage={firstLanguage}
      />
      <SubscriptionDialog
        isOpen={isSubscriptionDialogOpen}
        onClose={() => setIsSubscriptionDialogOpen(false)}
      />
    </div>
  );
};

export default ReviewDetailComponent; 